import React, { useContext, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import { DndContextWrapper } from './components/dnd-context-wrapper';
import { Sidebar } from './components/sidebar';
import { Chart } from './components/chart';
import { ChartHeader } from './components/chart-header';
import { GanttChartContext } from './context/gantt-chart.context';
import { StripsIndicatorsLayer } from './components/strips-indicators-layer';
import { PageLoader } from '../../app/components/page-loader';

import type { GanttItem } from './types';

export type ChartWrapperProps = {
  items: GanttItem[];
};

export const ChartWrapper: React.FC<ChartWrapperProps> = ({ items }) => {
  const { datesRangeByMonths, dateTrackerLabelRef, chartContainerRef, sidebarRef, todayIndex } =
    useContext(GanttChartContext);

  const headerContainerRef = useRef<HTMLDivElement>(null);

  const autoScroll = useRef<boolean>(true);
  const autoScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    if (!chartContainerRef?.current || !headerContainerRef.current || !autoScroll.current) {
      return;
    }

    const todayLine = document.getElementById('today-line');
    const scrollContainer = chartContainerRef.current;
    const headerContainer = headerContainerRef.current;

    if (!todayLine || !scrollContainer || !headerContainer) {
      return;
    }

    const targetRect = todayLine.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    const scrollLeft =
      targetRect.left -
      containerRect.left +
      scrollContainer.scrollLeft -
      (containerRect.width / 2 - targetRect.width / 2); // 48px * 7

    // set today scrollLeft to the center of the screen
    scrollContainer.scrollLeft = scrollLeft;
    headerContainer.scrollLeft = scrollLeft;

    if (autoScrollTimeout.current) {
      clearTimeout(autoScrollTimeout.current);
    }

    // TODO: improve performance and find a another way than using setTimeout
    autoScrollTimeout.current = setTimeout(() => {
      autoScroll.current = false;
    }, 2000);

    return () => {
      if (autoScrollTimeout.current) {
        clearTimeout(autoScrollTimeout.current);
        autoScrollTimeout.current = null;
      }
    };
  }, [chartContainerRef, todayIndex]);

  const handleChartScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (headerContainerRef?.current && chartContainerRef?.current) {
      headerContainerRef.current.scrollLeft = chartContainerRef.current.scrollLeft;
    }
  };

  return (
    <Container>
      {datesRangeByMonths ? (
        <>
          <HeaderContainer ref={headerContainerRef}>
            <DateTrackerLabel ref={dateTrackerLabelRef} />

            <ChartHeader />
          </HeaderContainer>

          <Wrapper>
            <Sidebar items={items} ref={sidebarRef} />

            <StripsIndicatorsLayer />

            <DndContextWrapper>
              <ChartContainer ref={chartContainerRef} onScroll={handleChartScroll}>
                <Chart />
              </ChartContainer>
            </DndContextWrapper>
          </Wrapper>
        </>
      ) : (
        <LoadingContainer>
          <PageLoader />
        </LoadingContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 100%;
  width: fit-content;
  flex: 1;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  height: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  align-items: stretch;
  max-width: 100%;
  flex: 1;
  margin-top: -3.2rem;
  background-color: var(--color-surfaces-bg-elevation-2);
`;

const ChartContainer = styled.div`
  min-width: 0;
  min-height: 100%;
  width: 100%;
  margin-inline-start: -24rem;
  margin-top: -6.4rem;
  padding-top: 6.4rem;
  overflow-x: auto;
  overflow-y: clip;
`;

const HeaderContainer = styled.div`
  max-width: 100%;
  overflow: auto;
  position: sticky;
  top: 0;
  min-height: 9.4rem;
  z-index: 11;
  pointer-events: none;
  background: linear-gradient(
    to bottom,
    var(--color-surfaces-bg-elevation-2) 0,
    var(--color-surfaces-bg-elevation-2) 6.4rem,
    transparent 6.4rem,
    transparent 9.4rem
  );

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const DateTrackerLabel = styled.div`
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
  background-color: black;
  color: var(--color-grayscale-white);
  text-align: center;
  padding: 0.8rem 1rem;
  border-radius: 0.6rem;
  font-size: 1.2rem;
  font-weight: 600;
  z-index: var(--layer-tooltip);
  width: auto;
  white-space: nowrap;
  opacity: 1;
`;
